import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, brl, openAuthedFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, FilePdf, EnvelopeSimple, CheckCircle, XCircle, Percent, ArrowsClockwise, Warning } from "@phosphor-icons/react";

export default function OrderView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [o, setO] = useState(null);
  const [sending, setSending] = useState(false);
  const [recalc, setRecalc] = useState(false);

  useEffect(() => { api.get(`/orders/${id}`).then((r) => setO(r.data)).catch(() => navigate("/pedidos")); }, [id, navigate]);

  const resend = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/orders/${id}/resend-email`);
      if (data.status === "success") toast.success("E-mail enviado com sucesso.");
      else if (data.status === "mocked") toast.info("E-mail simulado (Resend não configurado).");
      else toast.error("Falha ao enviar e-mail.");
    } finally { setSending(false); }
  };

  const doRecalc = async () => {
    setRecalc(true);
    try {
      const { data } = await api.post(`/orders/${id}/recalculate-commission`);
      setO(data);
      toast.success("Comissão recalculada.");
    } catch (e) { toast.error("Falha ao recalcular."); }
    finally { setRecalc(false); }
  };

  if (!o) return <div className="text-muted-foreground">Carregando...</div>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="order-view-page">
      <button onClick={() => navigate("/pedidos")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-zinc-900">
        <ArrowLeft size={18} /> Voltar
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Pedido</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950 font-mono">{o.numero}</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" data-testid="view-pdf-btn"
            onClick={() => openAuthedFile(`/orders/${id}/pdf`, `pedido-${o.numero}.pdf`)}>
            <FilePdf size={18} weight="bold" /> Ver PDF
          </Button>
          <Button className="bg-accent hover:bg-accent/90 text-white gap-2" disabled={sending} onClick={resend} data-testid="resend-email-btn">
            <EnvelopeSimple size={18} weight="bold" /> {sending ? "Enviando..." : "Enviar e-mail"}
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Card className="p-5 shadow-none border-border">
          <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">Cliente</p>
          <p className="font-semibold text-zinc-900">{o.cliente?.razao_social}</p>
          <p className="text-sm text-zinc-600">{o.cliente?.nome_fantasia}</p>
          <p className="text-sm text-zinc-500 font-mono mt-1">{o.cliente?.cnpj}</p>
          {(o.cliente_email || o.cliente?.email) && (
            <p className="text-sm text-zinc-600 mt-1">{o.cliente_email || o.cliente?.email}</p>
          )}
        </Card>
        <Card className="p-5 shadow-none border-border">
          <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">Condições</p>
          <div className="flex flex-wrap gap-2 mb-2">
            <Badge variant="secondary">{o.price_table_nome}</Badge>
            <Badge variant="outline" className={o.prazo_valido ? "border-accent text-accent" : "border-destructive text-destructive"}>
              {o.prazo_valido ? <CheckCircle size={14} className="mr-1" /> : <XCircle size={14} className="mr-1" />}
              {o.prazo} — {o.prazo_valido ? "PRAZO OK" : "INVÁLIDO"}
            </Badge>
          </div>
          <p className="text-sm text-zinc-600">Representante: {o.representante_nome}</p>
          {o.data_faturamento && <p className="text-sm text-zinc-600">Faturamento: {o.data_faturamento}</p>}
        </Card>
      </div>

      <Card className="shadow-none border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-950 text-white">
                {["Código", "Descrição", "Qtd", "Preço Un.", "Desc.%", "Subtotal"].map((h, i) => (
                  <th key={h} className={`px-4 py-2.5 text-xs font-semibold uppercase tracking-wider ${i >= 2 ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {o.itens.map((it, i) => (
                <tr key={`${it.produto_id}-${i}`}>
                  <td className="px-4 py-2.5 font-mono text-zinc-700">{it.codigo}</td>
                  <td className="px-4 py-2.5 text-zinc-800">{it.descricao}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{it.quantidade}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{brl(it.preco_unit)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{it.desconto_pct}%</td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-medium">{brl(it.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end p-4 border-t border-border">
          <div className="w-64 space-y-1.5 text-sm">
            <div className="flex justify-between text-zinc-600"><span>Subtotal</span><span className="tabular-nums">{brl(o.subtotal)}</span></div>
            <div className="flex justify-between text-zinc-600"><span>Desconto</span><span className="tabular-nums">- {brl(o.desconto_total)}</span></div>
            <div className="flex justify-between items-center pt-2 mt-1 border-t border-border">
              <span className="font-semibold text-zinc-900">Total</span>
              <span className="font-heading text-xl font-bold text-accent tabular-nums">{brl(o.total)}</span>
            </div>
          </div>
        </div>
      </Card>

      {isAdmin && (
        <Card className="p-5 shadow-none border-border bg-accent/5 border-accent/30" data-testid="commission-card">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-accent flex items-center gap-2"><Percent size={16} weight="bold" /> Comissão (visível apenas ao admin)</p>
            <Button variant="outline" size="sm" className="gap-2" disabled={recalc} onClick={doRecalc} data-testid="recalc-commission-btn">
              <ArrowsClockwise size={16} /> {recalc ? "Recalculando..." : "Recalcular"}
            </Button>
          </div>
          {o.comissao_sem_regra && (
            <div className="mb-3 px-3 py-2 rounded-md bg-amber-100 text-amber-800 text-sm flex items-center gap-2" data-testid="commission-no-rule-warning">
              <Warning size={16} weight="bold" /> Pedido sem regra de comissão cadastrada para esta combinação de tabela, prazo e desconto.
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-accent/20 text-left">
                {["Produto", "Categoria", "Desc.", "Líquido", "Comissão %", "Comissão"].map((h, i) => (
                  <th key={h} className={`py-1.5 text-xs font-semibold uppercase tracking-wider text-zinc-500 ${i >= 2 ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {o.itens.map((it, i) => (
                  <tr key={`c-${i}`} className="border-b border-accent/10">
                    <td className="py-1.5 text-zinc-700"><span className="font-mono text-xs text-zinc-500">{it.codigo}</span> {it.descricao}</td>
                    <td className="py-1.5"><Badge variant="outline" className="text-xs">{it.categoria_comissao || "Padrão"}</Badge></td>
                    <td className="py-1.5 text-right tabular-nums">{it.desconto_pct ?? 0}%</td>
                    <td className="py-1.5 text-right tabular-nums">{brl(it.subtotal)}</td>
                    <td className="py-1.5 text-right tabular-nums">{it.sem_regra ? <span className="text-amber-600 inline-flex items-center gap-1"><Warning size={13} /> sem regra</span> : `${it.comissao_pct ?? 0}%`}</td>
                    <td className="py-1.5 text-right tabular-nums font-medium text-accent">{brl(it.comissao_valor ?? 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end mt-3 pt-3 border-t border-accent/20">
            <span className="text-sm text-zinc-600 mr-3">Comissão total</span>
            <span className="font-heading text-xl font-bold text-accent tabular-nums" data-testid="order-commission-total">{brl(o.comissao_total ?? 0)}</span>
          </div>
        </Card>
      )}

      {o.observacoes && (
        <Card className="p-5 shadow-none border-border">
          <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1">Observações</p>
          <p className="text-sm text-zinc-700">{o.observacoes}</p>
        </Card>
      )}
    </div>
  );
}
